import { FeatureFlag, ResolverTag } from '@ps/web-core-enums';
import { StarsLink } from '@ps/web-shared-components';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Translation from '@swf/components/Translation/Translation';
import useFeature from '@swf/core/hooks/useFeature';
import useOpenWindow from '@swf/core/hooks/useOpenWindow';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';
import { required } from '@swf/core/utils/validators';

import FiscalCodeImage from '../../../../assets/fiscal-code.svg';
import { checkFiscalCode, fiscalCode, length } from '../../../../validators/fiscalCode';
import CTAButton from '../../../CTAButton/CTAButton';
import Container from '../../../Container/Container';
import InfoPrompt from '../../../InfoPrompt/InfoPrompt';
import TextInput from '../../../TextInput/TextInput';

import {
  TXTCLI_Fiscal_Code,
  TXTCLI_Please_enter_Fiscal_Code,
  TXTWCL_Please_enter_your_Fiscal_CodeX_ELL,
  TXTWCL_Please_enter_your_N0_character_ELL,
  TXTWCL_Please_log_in_to_an_existing_X_ELL,
  TXTWCL_The_Fiscal_Code_is_already_reg_ELL,
  TXTWCL_WhatXs_your_Fiscal_CodeX,
  TXTWCL_You_may_only_own_a_single_Star_ELL,
  TXTWCL_Your_code_can_be_found_here,
  TXTWCL_Your_details_6
} from './RMFFiscalCode.messages.json';
import { image, label } from './RMFFiscalCode.scss';

const parse = (value: string) => value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

const RMFFiscalCode: React.FC = () => {
  const { formatMessage } = useIntl();
  const dob = useSelector(select.account.getDateOfBirth);
  const options = useSelector(select.cms.getRequestOptions);
  const isAutofocusEnabled = useFeature(FeatureFlag.SwcSignupRmfAutofocus);

  const {
    values: { firstName, lastName, gender }
  } = useWizardContext();

  const openWindowContactSupport = useOpenWindow({
    resolverTag: ResolverTag.ContactSupport,
    centered: true
  });

  return (
    <Container
      page={RMFFiscalCode}
      headerProps={{
        title: <Translation {...TXTWCL_Your_details_6} />
      }}
      pageTopTitle={<Translation {...TXTWCL_WhatXs_your_Fiscal_CodeX} />}
      pageTopSubtitle={<Translation {...TXTWCL_Please_enter_your_Fiscal_CodeX_ELL} />}
    >
      <fieldset>
        <TextInput
          id="nationalDocumentNumber"
          label={TXTCLI_Fiscal_Code}
          labelClassName={label}
          labelContent={
            <InfoPrompt title={{ translation: TXTCLI_Fiscal_Code }}>
              <Translation tagName="p" {...TXTWCL_Your_code_can_be_found_here} />
              <FiscalCodeImage className={image} />
            </InfoPrompt>
          }
          placeholder={formatMessage(TXTWCL_Please_enter_your_N0_character_ELL, { 0: length })}
          maxLength={length}
          parse={parse}
          checkDisabled
          validators={[
            required(TXTCLI_Please_enter_Fiscal_Code),
            fiscalCode({ firstName, lastName, dob, gender }),
            checkFiscalCode(options, [
              TXTWCL_The_Fiscal_Code_is_already_reg_ELL,
              TXTWCL_You_may_only_own_a_single_Star_ELL,
              {
                ...TXTWCL_Please_log_in_to_an_existing_X_ELL,
                values: {
                  a: (msg: string) => (
                    <StarsLink onClick={openWindowContactSupport}>{msg}</StarsLink>
                  )
                }
              }
            ])
          ]}
          autoFocus={isAutofocusEnabled}
        />
      </fieldset>
      <CTAButton />
    </Container>
  );
};

RMFFiscalCode.displayName = 'RMFFiscalCode';

export default RMFFiscalCode;
